import * as React from 'react';
import { graphql, useStaticQuery } from 'gatsby';

import { ImageAsset } from './ImageAsset';
import styles from './embedded-asset.module.css';

import DefaultFileIcon from './file-icons/default.inline.svg';
import PDFFileIcon from './file-icons/pdf.inline.svg';
import ExcelFileIcon from './file-icons/excel.inline.svg';
import WordFileIcon from './file-icons/word.inline.svg';
import ZipFileIcon from './file-icons/zip.inline.svg';

interface EmbeddedAssetProps {
  contentfulId: string;
}

const IMG_REG = /^image\//;

const renderIcon = (contentType: string) => {
  switch (contentType) {
    case 'application/pdf': {
      return <PDFFileIcon />;
    }
    case 'application/msword':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
      return <WordFileIcon />;
    }
    case 'application/vnd.ms-excel':
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
      return <ExcelFileIcon />;
    }
    case 'application/zip': {
      return <ZipFileIcon />;
    }
    default: {
      return <DefaultFileIcon />;
    }
  }
};

export const EmbeddedAsset = ({ contentfulId }: EmbeddedAssetProps) => {
  const data = useStaticQuery<GatsbyTypes.EmbeddedAssetQuery>(graphql`
    query EmbeddedAsset {
      allContentfulAsset(
        filter: { file: { contentType: { regex: "image/" } } }
      ) {
        edges {
          node {
            contentful_id
            description
            title
            file {
              fileName
              contentType
              url
            }
          }
        }
      }
    }
  `);

  const asset = data.allContentfulAsset.edges.find(
    (n) => n.node.contentful_id === contentfulId
  )?.node;

  if (asset) {
    const { description, title: _title, file } = asset;

    const title = _title || file?.fileName;

    if (file && file.contentType && file.url) {
      if (IMG_REG.test(file?.contentType)) {
        return (
          <ImageAsset
            contentfulId={contentfulId}
            description={description}
            title={title}
          />
        );
      }

      return (
        <div className={[styles.container, file.contentType].join(' ')}>
          <div className={styles.icon}>{renderIcon(file.contentType)}</div>
          <div className={styles.details}>
            <h4 className={styles.title}>{title}</h4>
            {description && <p className={styles.description}>{description}</p>}
          </div>
          <div className={styles.download}>
            <a
              href={file.url}
              className={styles.downloadBtn}
              download
              rel="noopener noreferrer nofollow"
            >
              Download
            </a>
          </div>
        </div>
      );
    }

    console.warn(
      `Missing asset 'file' data for contentful_id: ${contentfulId}`
    );
    return null;
  }

  console.warn(`No asset found for contentful_id: ${contentfulId}`);
  return null;
};
